import React, { useState } from "react";
import { Send } from "lucide-react";
import Container from "../components/Container";
import Breadcrumb from "../components/Breadcrumb";
import { cls } from "../utils/helpers";
import { CONTACT_EMAIL } from "../utils/constants";

export default function AccountDeletionPage({ p, t }) {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function submitDeletion(e) {
    e.preventDefault();
    if (!email || !consent) {
      alert("Please provide your registered email and confirm your request.");
      return;
    }

    const mailSubject = encodeURIComponent("Account Deletion Request");
    const body = encodeURIComponent(
      [
        "Account Deletion Request",
        "",
        `Registered Email: ${email}`,
        "",
        `Reason: ${reason || "Not specified"}`,
        "",
        "I confirm that I want to permanently delete my account and all associated data.",
      ].join("\n")
    );

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${mailSubject}&body=${body}`;
    setSubmitted(true);
  }

  return (
    <>
      <Container>
        <Breadcrumb
          items={[
            { label: t("crumb.home"), href: "/" },
            { label: "Account Deletion" },
          ]}
          p={p}
        />
      </Container>

      <section className={p.hero}>
        <Container>
          <div className="py-12">
            <h1 className={cls("text-3xl font-extrabold", p.cardTitle)}>
              Account Deletion Request
            </h1>
            <p className={cls("mt-2 max-w-3xl", p.paragraph)}>
              We're sorry to see you go. Please fill out the form below to request the deletion of your account and all associated data.
            </p>
          </div>
        </Container>
      </section>

      <section className="py-10">
        <Container>
          {submitted ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-green-800 dark:text-green-300 mb-4">
                Request Submitted
              </h2>
              <p className="text-green-700 dark:text-green-400 mb-4">
                Your account deletion request has been submitted. We will process your request and contact you at the provided email address.
              </p>
              <p className="text-sm text-green-600 dark:text-green-500">
                Please allow up to 30 days for processing.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submitDeletion}
              className="max-w-2xl mx-auto bg-white/70 backdrop-blur rounded-2xl p-6 ring-1 ring-indigo-200"
            >
              <div className="mb-4">
                <label className="text-sm font-semibold block mb-2">
                  Registered Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-indigo-200 bg-indigo-50 p-3"
                  placeholder="your.email@example.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the email address associated with your account
                </p>
              </div>

              <div className="mb-4">
                <label className="text-sm font-semibold block mb-2">
                  Reason for Deletion (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-indigo-200 bg-indigo-50 p-3"
                  placeholder="Please let us know why you're leaving..."
                />
              </div>

              <div className="mb-6">
                <label className="inline-flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <span>
                    I understand that deleting my account is permanent and will result in the loss of all my data, including settings, preferences, and any content associated with my account. This action cannot be undone.
                  </span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className={cls("inline-flex items-center gap-2", p.ctaPrimary)}
                >
                  <Send size={16} /> Submit Deletion Request
                </button>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  <strong>Important:</strong> Once your account is deleted, all your personal data will be permanently removed from our systems within 30 days. This includes your profile information, usage history, and any associated content.
                </p>
              </div>
            </form>
          )}
        </Container>
      </section>
    </>
  );
}
